import React, { useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import clsx from "clsx";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Link, useHistory, useParams, useRouteMatch } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  QUERY_CHAT_MESSAGE_TEMPLATES,
  QUERY_CLIENT,
} from "../../../../config/graphql/query";
import {
  MUTATION_RESET_CHAT_MESSAGE_TEMPLATE,
  MUTATION_SAVE_CHAT_MESSAGE_TEMPLATE,
} from "../../../../config/graphql/mutation";
import { useClientId } from "../hooks/useClient";
import { getApiUrl } from "../../../../lib/common/getApiUrl";
import useHasRole from "../../../../lib/hooks/useHasRole";

import { CHAT_LANGUAGES } from "./ChatLanguagesSetting";
import { editedByName } from "./TemplateList";
import useChatLanguages from "./useChatLanguages";

type FieldValues = { message: Partial<ChatMessageText> };

const VARIABLE_PATTERN = /\{\{\s*([^{}\s]+)\s*\}\}/g;

const DOOR_OPEN_PATTERN = /\{\{\s*doorOpenUrl\s*\}\}/;

/** Sample values for the preview only — the server fills the real ones. */
const SAMPLE_VALUES: Record<string, string> = {
  name: "Sam Taylor",
  visitorName: "Alex Morgan",
  attendeeName: "Alex Morgan",
  doorOpenUrl: `${getApiUrl()}/s/Xy7kP2mQ9aBc`,
};

const fillSample = (text: string) =>
  text.replace(VARIABLE_PATTERN, (match, name) => SAMPLE_VALUES[name] ?? match);

/** As the server sends it: door line appended, or its line dropped when there is no door. */
const asSent = (text: string, doorLine?: string | null) => {
  if (!doorLine) {
    return text
      .split("\n")
      .filter((line) => !DOOR_OPEN_PATTERN.test(line))
      .join("\n");
  }

  return DOOR_OPEN_PATTERN.test(text) ? text : `${text}\n${doorLine}`;
};

const unknownVariables = (text: string, allowed: string[]) =>
  Array.from(
    new Set(
      Array.from(text.matchAll(VARIABLE_PATTERN), (match) => match[1]).filter(
        (name) => !allowed.includes(name),
      ),
    ),
  );

const TemplateEditor = () => {
  const { t } = useTranslation(["chatLanguages", "client"]);
  const history = useHistory();
  const { url } = useRouteMatch();
  const { template: name } = useParams<{ template: ChatMessageTemplateName }>();
  const listUrl = url.slice(0, url.lastIndexOf("/"));

  const clientId = useClientId();
  const languages = useChatLanguages();
  const isAdmin = useHasRole(["ADMIN"]);

  const context = useMemo(
    () => ({ headers: { ClientId: clientId } }),
    [clientId],
  );

  const { data } = useQuery<{ chatMessageTemplates: IChatMessageTemplate[] }>(
    QUERY_CHAT_MESSAGE_TEMPLATES,
    { skip: !clientId, context, fetchPolicy: "network-only" },
  );

  const { data: clientData } = useQuery<{ client: IClient }>(QUERY_CLIENT, {
    variables: { id: clientId },
    skip: !clientId,
  });

  const template = data?.chatMessageTemplates.find(
    (item) => item.template === name,
  );

  const refetchQueries = [{ query: QUERY_CHAT_MESSAGE_TEMPLATES, context }];

  const [onSave] = useMutation(MUTATION_SAVE_CHAT_MESSAGE_TEMPLATE, {
    context,
    refetchQueries,
  });
  const [onReset] = useMutation(MUTATION_RESET_CHAT_MESSAGE_TEMPLATE, {
    context,
    refetchQueries,
  });

  const methods = useForm<FieldValues>({ shouldFocusError: false });

  useEffect(() => {
    if (template) {
      methods.reset({ message: template.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const message = methods.watch("message");

  const textareas = useRef<Partial<Record<ChatLanguage, HTMLTextAreaElement>>>(
    {},
  );
  const [activeLanguage, setActiveLanguage] = useState<ChatLanguage>();
  const [confirmReset, setConfirmReset] = useState(false);

  // Same trail as every other page opened from a client tab (e.g. an SMS template).
  const breadcrumbs = (
    <nav aria-label="breadcrumb">
      <ol className="breadcrumb my-3">
        {/* `/clients` is ADMIN only. */}
        {isAdmin && (
          <li className="breadcrumb-item">
            <Link to="/clients">
              {t("client:client.clientCreate.nav.clients")}
            </Link>
          </li>
        )}
        <li className="breadcrumb-item">
          {/* /clients/:id or /client — whichever this page was opened from. */}
          <Link to={listUrl.slice(0, listUrl.lastIndexOf("/"))}>
            {clientData?.client?.title ?? "Client"}
          </Link>
        </li>
        <li className="breadcrumb-item">
          <Link to={listUrl}>
            {t("client:client.clientCreate.nav.chatMessages")}
          </Link>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          {t(`chatLanguages:templates.type.${name}`)}
        </li>
      </ol>
    </nav>
  );

  if (!template || !languages) {
    return <div className="container-fluid">{breadcrumbs}</div>;
  }

  const allowed = template.variables;

  const doorOpenSent = template.doorOpenReceptions.some(
    (reception) => !!reception.door,
  );

  const doorLine = (language: ChatLanguage) =>
    doorOpenSent ? template.doorOpenLine?.[language] : null;

  const offeredVariables = allowed.filter(
    (variable) => variable !== "doorOpenUrl" || doorOpenSent,
  );

  // Door line included, so the whole message can be edited.
  const builtIn = (language: ChatLanguage) =>
    asSent(template.defaultMessage[language], doorLine(language));

  const unknownFor = (language: ChatLanguage) =>
    unknownVariables(message?.[language] || "", allowed);

  const hasUnknown = languages.some(
    (language) => unknownFor(language).length > 0,
  );

  // Text kept for languages that are switched off right now — saved, not sent.
  const storedElsewhere = CHAT_LANGUAGES.filter(
    (language) =>
      !languages.includes(language) && template.message[language]?.trim(),
  );

  const insertVariable = (variable: string) => {
    const language = activeLanguage || languages[0];
    const textarea = textareas.current[language];
    const current = methods.getValues(`message.${language}`) || "";
    const token = `{{${variable}}}`;

    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? current.length;

    methods.setValue(
      `message.${language}`,
      current.slice(0, start) + token + current.slice(end),
      { shouldDirty: true },
    );

    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const preview = languages
    .map((language) =>
      fillSample(
        message?.[language]?.trim()
          ? asSent(message[language] as string, doorLine(language))
          : builtIn(language),
      ),
    )
    .join("\n\n———\n\n");

  const onSubmit = async (values: FieldValues) => {
    // Only the languages on screen; a switched-off language keeps its text on the server.
    const payload: Partial<ChatMessageText> = Object.fromEntries(
      languages.map((language) => [language, values.message?.[language] || ""]),
    );

    try {
      await onSave({ variables: { template: name, message: payload } });
      toast.success<string>(t("chatLanguages:templates.toast.saved"));
    } catch (error: any) {
      toast.error<string>(
        error?.message || t("chatLanguages:templates.toast.saveError"),
      );
    }
  };

  const onConfirmReset = async () => {
    setConfirmReset(false);

    try {
      await onReset({ variables: { template: name } });
      toast.success<string>(t("chatLanguages:templates.toast.reset"));
      history.push(listUrl);
    } catch (error: any) {
      toast.error<string>(
        error?.message || t("chatLanguages:templates.toast.resetError"),
      );
    }
  };

  return (
    <div className="container-fluid">
      {breadcrumbs}
      <h5>
        {t("chatLanguages:templates.editor.title", {
          type: t(`chatLanguages:templates.type.${name}`),
        })}
      </h5>
      <p className="text-primary mb-3">
        {t("chatLanguages:templates.editor.subtitle")}
        {template.custom && (
          <small className="text-muted d-block">
            {t("chatLanguages:templates.editor.lastEdited", {
              name: editedByName(template) || "—",
              date: template.updatedAt
                ? dayjs(template.updatedAt).format("DD.MM.YYYY HH:mm")
                : "—",
            })}
          </small>
        )}
      </p>

      <div className="row">
        <div className="col-xl-7">
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="mb-3">
              <span className="mr-2">
                {t("chatLanguages:templates.editor.variables")}
              </span>
              {offeredVariables.map((variable) => (
                <Button
                  key={variable}
                  size="sm"
                  variant="outline-primary"
                  className="mr-2 mb-1"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertVariable(variable)}
                >
                  {t(`chatLanguages:templates.variable.${variable}`)}{" "}
                  <code>{`{{${variable}}}`}</code>
                </Button>
              ))}
              <small className="text-muted d-block">
                {t("chatLanguages:templates.editor.variablesHint")}
              </small>
            </div>

            {languages.map((language) => {
              const { ref, ...field } = methods.register(`message.${language}`);
              const unknown = unknownFor(language);

              return (
                <div key={language} className="form-group">
                  <div className="d-flex justify-content-between align-items-end mb-1">
                    <label htmlFor={`chatMessage-${language}`} className="mb-0">
                      {t(`chatLanguages:language.${language}`)}
                    </label>
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0"
                      onClick={() =>
                        methods.setValue(
                          `message.${language}`,
                          builtIn(language),
                          { shouldDirty: true },
                        )
                      }
                    >
                      {t("chatLanguages:templates.button.startFromDefault")}
                    </Button>
                  </div>
                  <textarea
                    {...field}
                    ref={(element) => {
                      ref(element);
                      textareas.current[language] = element || undefined;
                    }}
                    id={`chatMessage-${language}`}
                    rows={4}
                    maxLength={2000}
                    placeholder={builtIn(language)}
                    onFocus={() => setActiveLanguage(language)}
                    className={clsx("form-control", {
                      "is-invalid": unknown.length > 0,
                    })}
                  />
                  {unknown.length > 0 ? (
                    <div className="invalid-feedback">
                      {t("chatLanguages:templates.editor.unknownVariables", {
                        names: unknown.map((item) => `{{${item}}}`).join(", "),
                      })}
                    </div>
                  ) : (
                    <small className="text-muted">
                      {t("chatLanguages:templates.editor.emptyHint")}
                    </small>
                  )}
                </div>
              );
            })}

            {storedElsewhere.length > 0 && (
              <p className="small text-muted">
                {t("chatLanguages:templates.editor.storedElsewhere", {
                  languages: storedElsewhere
                    .map((language) => t(`chatLanguages:language.${language}`))
                    .join(", "),
                })}
              </p>
            )}

            <div className="d-flex mt-2 mb-3">
              <button
                type="submit"
                className="btn btn-primary mr-2"
                disabled={methods.formState.isSubmitting || hasUnknown}
              >
                {t("chatLanguages:templates.button.save")}
              </button>
              {template.custom && (
                <Button
                  variant="outline-danger"
                  onClick={() => setConfirmReset(true)}
                >
                  {t("chatLanguages:templates.button.reset")}
                </Button>
              )}
            </div>
          </form>
        </div>

        <div className="col-xl-5">
          <h6>{t("chatLanguages:templates.editor.preview")}</h6>
          <small className="text-muted d-block mb-2">
            {t("chatLanguages:templates.editor.previewHint")}
          </small>
          <div
            className="border rounded p-3 bg-light"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {preview}
          </div>

          {template.doorOpenLine && (
            <div className="mt-3">
              <h6>{t("chatLanguages:templates.doorOpen.title")}</h6>
              {template.doorOpenReceptions.length > 0 ? (
                <ul className="list-unstyled small mb-1">
                  {template.doorOpenReceptions.map((reception) => (
                    <li key={reception.screen}>
                      <span
                        className={clsx(
                          "mr-2",
                          reception.door ? "text-success" : "text-danger",
                        )}
                      >
                        {reception.door ? "✔" : "✖"}
                      </span>
                      <strong className="mr-2">{reception.title}</strong>
                      <span className="text-muted">
                        {reception.door
                          ? t(
                              reception.withoutScreen
                                ? "chatLanguages:templates.doorOpen.sentOnlyDoor"
                                : "chatLanguages:templates.doorOpen.sent",
                              { door: reception.door },
                            )
                          : t(
                              `chatLanguages:templates.doorOpen.reason.${reception.reason}`,
                            )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="small text-muted mb-1">
                  {t("chatLanguages:templates.doorOpen.noReceptions")}
                </p>
              )}
              <small className="text-muted">
                {t("chatLanguages:templates.doorOpen.groupHint")}
              </small>
            </div>
          )}
        </div>
      </div>

      <Modal show={confirmReset} onHide={() => setConfirmReset(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("chatLanguages:templates.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("chatLanguages:templates.modal.body")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmReset(false)}>
            {t("chatLanguages:templates.modal.cancel")}
          </Button>
          <Button variant="danger" onClick={onConfirmReset}>
            {t("chatLanguages:templates.modal.confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TemplateEditor;
